// @ts-nocheck
import { useState } from 'react';
// @ts-nocheck
import { IconPhoto, IconMessageCircle, IconSettings } from '@tabler/icons-react';
import { Anchor, Breadcrumbs, Card, Code, Divider, Pagination, Stack, Stepper, Tabs, Text, Title } from '@/components/ui';

export default function NavigationPage() {
  const [activeTab, setActiveTab] = useState<string | null>('gallery');
  const [active, setActive] = useState(1);
  const [activePage, setActivePage] = useState(1);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Navigation Components</Title>
        <Text c="dimmed" size="sm">
          Components for navigation and wayfinding - tabs, steppers, breadcrumbs, and pagination
        </Text>
      </Stack>

      {/* Tabs */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Tabs</Title>
            <Text size="sm" c="dimmed">
              Organize content into multiple sections accessible via tabs
            </Text>
          </Stack>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="gallery" leftSection={<IconPhoto size={16} />}>
                Gallery
              </Tabs.Tab>
              <Tabs.Tab value="messages" leftSection={<IconMessageCircle size={16} />}>
                Messages
              </Tabs.Tab>
              <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
                Settings
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="gallery" pt="md">
              <Text size="sm">Gallery tab content</Text>
            </Tabs.Panel>
            <Tabs.Panel value="messages" pt="md">
              <Text size="sm">Messages tab content</Text>
            </Tabs.Panel>
            <Tabs.Panel value="settings" pt="md">
              <Text size="sm">Settings tab content</Text>
            </Tabs.Panel>
          </Tabs>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Tabs value={activeTab} onChange={setActiveTab}>
  <Tabs.List>
    <Tabs.Tab value="gallery" leftSection={<IconPhoto size={16} />}>
      Gallery
    </Tabs.Tab>
    <Tabs.Tab value="messages" leftSection={<IconMessageCircle size={16} />}>
      Messages
    </Tabs.Tab>
    <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
      Settings
    </Tabs.Tab>
  </Tabs.List>

  <Tabs.Panel value="gallery" pt="md">
    Gallery tab content
  </Tabs.Panel>
  <Tabs.Panel value="messages" pt="md">
    Messages tab content
  </Tabs.Panel>
</Tabs>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Stepper */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Stepper</Title>
            <Text size="sm" c="dimmed">
              Display progress through numbered steps
            </Text>
          </Stack>

          <Stepper active={active} onStepClick={setActive}>
            <Stepper.Step label="First step" description="Create an account">
              Step 1 content: Create an account
            </Stepper.Step>
            <Stepper.Step label="Second step" description="Verify email">
              Step 2 content: Verify your email
            </Stepper.Step>
            <Stepper.Step label="Final step" description="Get started">
              Step 3 content: Start using the platform
            </Stepper.Step>
            <Stepper.Completed>
              Completed! You're all set.
            </Stepper.Completed>
          </Stepper>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Stepper active={active} onStepClick={setActive}>
  <Stepper.Step label="First step" description="Create an account">
    Step 1 content
  </Stepper.Step>
  <Stepper.Step label="Second step" description="Verify email">
    Step 2 content
  </Stepper.Step>
  <Stepper.Step label="Final step" description="Get started">
    Step 3 content
  </Stepper.Step>
  <Stepper.Completed>
    Completed!
  </Stepper.Completed>
</Stepper>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Breadcrumbs */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Breadcrumbs</Title>
            <Text size="sm" c="dimmed">
              Show the current page's location within a navigational hierarchy
            </Text>
          </Stack>

          <Breadcrumbs
            items={[
              { label: 'Projects', href: '#' },
              { label: 'Website Redesign', href: '#' },
              { label: 'Tasks' },
            ]}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`// The themed Breadcrumbs takes an items array (adds a Home crumb by default)
<Breadcrumbs
  items={[
    { label: 'Projects', href: '#' },
    { label: 'Website Redesign', href: '#' },
    { label: 'Tasks' }, // last item, no href = current page
  ]}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Pagination */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Pagination</Title>
            <Text size="sm" c="dimmed">
              Navigate through pages of content
            </Text>
          </Stack>

          <Stack gap="md">
            <Pagination total={10} value={activePage} onChange={setActivePage} />
            <Pagination total={10} value={activePage} onChange={setActivePage} withEdges />
            <Pagination total={20} value={activePage} onChange={setActivePage} siblings={2} />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Pagination total={10} value={activePage} onChange={setActivePage} />
<Pagination total={10} value={activePage} onChange={setActivePage} withEdges />
<Pagination total={20} siblings={2} />`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
